// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { illiquidsApi } from '@shared/data-access/api/engine/illiquids-api';
import {
  CapitalCallSubtype,
  DistributionSubtype,
  IlliquidAssetCurrency,
  IlliquidAssetEventType,
  type IlliquidAsset,
  type IlliquidAssetEvent,
} from '@shared/data-access/types/engine-types';
import { useConfirmPopup } from '@shared/ui-kit/components';
import { useSnackbar } from '@shared/ui-kit/providers/snackbar-provider';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useIlliquidAssetsClientId } from '../../hooks/use-illiquid-assets-client-id';
import {
  illiquidEventFormSchema,
  type IlliquidEventFormData,
} from '../../types/illiquid-event-form';
import { buildIlliquidEventCreateBody } from '../../utils/build-illiquid-event-create-body';
import { groupIlliquidEventsByYear } from '../../utils/group-illiquid-events-by-year';

export type IlliquidAssetFinancialEventsSectionProps =
  | {
      readonly mode?: 'remote';
      readonly asset: IlliquidAsset;
    }
  | {
      readonly mode: 'draft';
      readonly draftEvents: IlliquidAssetEvent[];
      readonly onDraftEventsChange: (events: IlliquidAssetEvent[]) => void;
      readonly defaultCurrency: IlliquidAssetCurrency;
    };

const buildDraftEvent = (
  data: IlliquidEventFormData,
  tempId: number,
): IlliquidAssetEvent => {
  const base = {
    id: tempId,
    illiquid_asset_id: 0,
    event_date: data.eventDate,
    amount: data.amount,
    currency: data.currency,
    description: data.description || null,
    created_at: '',
    updated_at: '',
  };

  switch (data.eventType) {
    case IlliquidAssetEventType.Acquisition:
      return {
        ...base,
        event_type: IlliquidAssetEventType.Acquisition,
        subtype: null,
      };

    case IlliquidAssetEventType.Valuation:
      return {
        ...base,
        event_type: IlliquidAssetEventType.Valuation,
        subtype: null,
      };

    case IlliquidAssetEventType.CapitalCall:
      return {
        ...base,
        event_type: IlliquidAssetEventType.CapitalCall,
        subtype: data.subtype,
      };

    case IlliquidAssetEventType.Distribution:
      return {
        ...base,
        event_type: IlliquidAssetEventType.Distribution,
        subtype: data.subtype,
      };
  }
};

export const useIlliquidAssetFinancialEventsSection = (
  props: IlliquidAssetFinancialEventsSectionProps,
) => {
  const { t } = useTranslation();

  const { enqueueSnackbar } = useSnackbar();

  const showConfirm = useConfirmPopup();

  const clientId = useIlliquidAssetsClientId();

  const isDraft = props.mode === 'draft';

  const [createIlliquidAssetEvent, createEventState] =
    illiquidsApi.useCreateIlliquidAssetEventMutation();

  const [deleteIlliquidAssetEvent, deleteEventState] =
    illiquidsApi.useDeleteIlliquidAssetEventMutation();

  const [showForm, setShowForm] = useState(false);

  const [deletingEventId, setDeletingEventId] = useState<number | null>(null);

  const todayIso = useMemo(() => dayjs().format('YYYY-MM-DD'), []);

  const defaultCurrency = useMemo(() => {
    if (isDraft) {
      return props.defaultCurrency;
    }

    return (props.asset.local_currency_values?.currency ??
      props.asset.currency ??
      IlliquidAssetCurrency.EUR) as IlliquidAssetCurrency;
  }, [isDraft, props]);

  const currencyOptions = useMemo(() => Object.values(IlliquidAssetCurrency), []);

  const eventTypeOptions = useMemo(
    () => [
      {
        value: IlliquidAssetEventType.Acquisition,
        label: t('illiquid-asset-financial-events.event-type.acquisition', {
          ns: 'components',
        }),
      },
      {
        value: IlliquidAssetEventType.Valuation,
        label: t('illiquid-asset-financial-events.event-type.valuation', {
          ns: 'components',
        }),
      },
      {
        value: IlliquidAssetEventType.CapitalCall,
        label: t('illiquid-asset-financial-events.event-type.capital-call', {
          ns: 'components',
        }),
      },
      {
        value: IlliquidAssetEventType.Distribution,
        label: t('illiquid-asset-financial-events.event-type.distribution', {
          ns: 'components',
        }),
      },
    ],
    [t],
  );

  const capitalCallSubtypeOptions = useMemo(
    () => [
      {
        value: CapitalCallSubtype.Executed,
        label: t('illiquid-asset-financial-events.subtype.capital-call.executed', {
          ns: 'components',
        }),
      },
      {
        value: CapitalCallSubtype.Planned,
        label: t('illiquid-asset-financial-events.subtype.capital-call.planned', {
          ns: 'components',
        }),
      },
    ],
    [t],
  );

  const distributionSubtypeOptions = useMemo(
    () => [
      {
        value: DistributionSubtype.Liquidity,
        label: t('illiquid-asset-financial-events.subtype.distribution.liquidity', {
          ns: 'components',
        }),
      },
      {
        value: DistributionSubtype.PartialSale,
        label: t(
          'illiquid-asset-financial-events.subtype.distribution.partial-sale',
          { ns: 'components' },
        ),
      },
      {
        value: DistributionSubtype.FullSale,
        label: t('illiquid-asset-financial-events.subtype.distribution.full-sale', {
          ns: 'components',
        }),
      },
      {
        value: DistributionSubtype.Dividend,
        label: t('illiquid-asset-financial-events.subtype.distribution.dividend', {
          ns: 'components',
        }),
      },
    ],
    [t],
  );

  const defaultValues = useMemo(
    () =>
      ({
        eventType: IlliquidAssetEventType.Valuation,
        eventDate: todayIso,
        amount: undefined,
        currency: defaultCurrency,
        description: '',
      } as unknown as IlliquidEventFormData),
    [defaultCurrency, todayIso],
  );

  const form = useForm<IlliquidEventFormData>({
    resolver: zodResolver(illiquidEventFormSchema),
    mode: 'onChange',
    defaultValues,
  });

  const resetForm = useCallback(
    (values: IlliquidEventFormData = defaultValues) => {
      form.reset(values);
      form.clearErrors();
    },
    [defaultValues, form],
  );

  const selectedEventType = form.watch('eventType');

  const formValues = form.watch();

  const selectedSubtype = 'subtype' in formValues ? formValues.subtype : undefined;

  const events = useMemo(
    () => (isDraft ? props.draftEvents : props.asset.events ?? []),
    [isDraft, props],
  );

  const groupedEvents = useMemo(() => groupIlliquidEventsByYear(events), [events]);

  const onEventTypeChange = useCallback(
    (eventType: IlliquidAssetEventType) => {
      const current = form.getValues();

      const shared = {
        eventDate: current.eventDate,
        amount: current.amount,
        currency: current.currency,
        description: current.description ?? '',
      };

      if (eventType === IlliquidAssetEventType.CapitalCall) {
        resetForm({
          ...shared,
          eventType,
          subtype: CapitalCallSubtype.Executed,
        });

        return;
      }

      if (eventType === IlliquidAssetEventType.Distribution) {
        resetForm({
          ...shared,
          eventType,
          subtype: DistributionSubtype.Liquidity,
        });

        return;
      }

      resetForm({
        ...shared,
        eventType,
      });
    },
    [form, resetForm],
  );

  const onToggleForm = useCallback(() => {
    setShowForm(current => {
      if (!current) {
        resetForm();
      }

      return !current;
    });
  }, [resetForm]);

  const onCancelForm = useCallback(() => {
    resetForm();
    setShowForm(false);
  }, [resetForm]);

  const onSubmit = form.handleSubmit(async data => {
    if (isDraft) {
      const nextEvent = buildDraftEvent(data, -Date.now());

      props.onDraftEventsChange([...props.draftEvents, nextEvent]);

      resetForm();

      return;
    }

    try {
      await createIlliquidAssetEvent({
        asset_id: props.asset.id,
        body: buildIlliquidEventCreateBody(data),
        client_id: clientId,
      }).unwrap();

      resetForm();
      setShowForm(false);

      enqueueSnackbar(
        t('illiquid-asset-financial-events.messages.event-saved', {
          ns: 'components',
        }),
        { variant: 'success' },
      );
    } catch {
      enqueueSnackbar(
        t('illiquid-asset-financial-events.messages.event-save-error', {
          ns: 'components',
        }),
        { variant: 'error' },
      );
    }
  });

  const onDeleteEvent = useCallback(
    (event: IlliquidAssetEvent) => {
      if (isDraft) {
        props.onDraftEventsChange(
          props.draftEvents.filter(item => item.id !== event.id),
        );

        return;
      }

      showConfirm({
        title: t('illiquid-asset-financial-events.delete-confirm-title', {
          ns: 'components',
        }),
        message: t('illiquid-asset-financial-events.delete-confirm-message', {
          ns: 'components',
        }),
        confirmText: t('illiquid-asset-financial-events.delete-confirm-action', {
          ns: 'components',
        }),
        onConfirm: async () => {
          setDeletingEventId(event.id);

          try {
            await deleteIlliquidAssetEvent({
              asset_id: props.asset.id,
              event_id: event.id,
              client_id: clientId,
            }).unwrap();

            enqueueSnackbar(
              t('illiquid-asset-financial-events.messages.event-deleted', {
                ns: 'components',
              }),
              { variant: 'success' },
            );
          } catch {
            enqueueSnackbar(
              t('illiquid-asset-financial-events.messages.event-delete-error', {
                ns: 'components',
              }),
              { variant: 'error' },
            );
          } finally {
            setDeletingEventId(null);
          }
        },
      });
    },
    [
      clientId,
      deleteIlliquidAssetEvent,
      enqueueSnackbar,
      isDraft,
      props,
      showConfirm,
      t,
    ],
  );

  return {
    t,
    form,
    showForm,
    onToggleForm,
    onCancelForm,
    onSubmit,
    onDeleteEvent,
    onEventTypeChange,
    selectedEventType,
    selectedSubtype,
    groupedEvents,
    hasEvents: events.length > 0,
    isSaving: !isDraft && createEventState.isLoading,
    deletingEventId,
    isDeleting: deleteEventState.isLoading,
    currencyOptions,
    eventTypeOptions,
    capitalCallSubtypeOptions,
    distributionSubtypeOptions,
    defaultCurrency,
    displayCurrency: defaultCurrency,
  } as const;
};
